import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { type ThemeType } from '@meitu/image-matting';
import { uploadBase64ToObs } from '@meitu/saas-editor-core/es/utils/upload-kit';
import { IImageLayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { checkTeamIsEnterprise } from '@meitu/xdesign-widgets';
import Loading from '@meitu/xdesign-widgets/dist/loading';
import { AI_MATTING_IMAGE } from '@/editor/extensions/ai-matting/components/ai-matting-extension';
import {
    destroyMattingSDK,
    extractResult,
    initMattingSDK,
    tryMatting,
    updateTheme,
} from '@/editor/extensions/ai-matting/image-matting-sdk';
import { useMattingPluginHelper } from '@/editor/extensions/ai-matting/use-matting-plugin-helper';
import { events, MATTING_PLUGIN_APPLY_EVENT, MATTING_PLUGIN_CANCEL_EVENT, PLUGIN_CLOSE_EVENT } from '@/events-bus';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { useEditorModel } from '@/store/models/editor-model';
import { getPluginModel, usePluginModel } from '@/store/models/plugin-model';
import { getQuery } from '@/utils/query';
import styles from './index.module.less';
import AIMattingBackTitle from './matting-back-title';
import AIMattingOperateBtns from './matting-operate-btn';

const RenderMattingImagePlugin = () => {
    const { $t } = useLocaleStore();
    const isMatting = usePluginModel((state) => state.isMatting);
    const mattingPluginHelper = useMattingPluginHelper();
    const processingMattingImgInfo = usePluginModel((state) => state.processingMattingImgInfo);
    const isMattingMainLayer = usePluginModel((state) => state.isMattingMainLayer);
    const mattingCanApply = usePluginModel((state) => state.aiMattingCanApply);
    const setMattingCanApply = usePluginModel((state) => state.setAiMattingCanApply);
    const theme = useEditorModel((state) => state.theme);

    const anchorEl = useRef<HTMLDivElement | null>(null);
    const [isSDKLoaded, setIsSDKLoaded] = useState(false);

    // 处理应用回调
    const handleApplyCb = async (resultBase64: string, mattingInfo: any) => {
        const originLayer = cloneDeep(
            isMattingMainLayer ? editor.mainLayer.getMainLayer() : editor.services.frame.getActivePickLayer(),
        )!;
        const newLayer = cloneDeep(originLayer);
        const { url: resultUrl } = await uploadBase64ToObs(resultBase64);
        if (newLayer) {
            const newInfoData = newLayer.data as IImageLayerProps;
            newInfoData.url = resultUrl;
            editor.imageLayer.updateLayer(
                newLayer,
                originLayer,
                true,
                isMattingMainLayer ? 'matting' : 'matting_plugin',
            );
        }

        Loading.hidden();
        // 应用完，关闭编辑器抠图功能tab
        events.emit(MATTING_PLUGIN_APPLY_EVENT);
        exitPlugin(resultUrl);

        // // 埋点相关
        // window._stat_track('tpbj_subfunction_tick', {
        //     func: '30_153',
        // });
        // editor.history.pushFunc('30_153');
        // 上传结束后读取最新状态，避免 SDK 回调持有的旧快照覆盖其他功能记录。
        const { funcUsedMap, setFuncUsedMap } = getPluginModel();
        const key = `aisc_${newLayer.id}`;
        setFuncUsedMap({
            ...funcUsedMap,
            [key]: {
                ...funcUsedMap[key],
                [AI_MATTING_IMAGE]: {
                    func: AI_MATTING_IMAGE,
                    url: resultUrl,
                },
            },
        });
    };

    const mattingImage = () => {
        const { data, layerType } = editor.services.frame.getActivePickLayer() || editor.mainLayer.getMainLayer()!;
        if (![LayerType.IMAGE_LAYER, LayerType.MAIN_LAYER].includes(layerType)) {
            return null;
        }
        const { url, naturalWidth, naturalHeight } = data as IImageLayerProps;
        const mattingOption = {
            name: '',
            size: 0,
            url: url,
            width: naturalWidth,
            height: naturalHeight,
        };

        const mattindData = processingMattingImgInfo || mattingOption;
        tryMatting(mattindData, handleApplyCb);
    };

    const onload = () => {
        console.log('Image Matting SDK loaded.');
        Loading.hidden();
        setIsSDKLoaded(true);

        mattingImage();
    };

    // 下载状态变更
    const onDownloadableUpdate = (data: boolean) => {
        setMattingCanApply(data);
    };

    // 抠图状态变更
    const onMattingStatusUpdate = (data: string) => {
        if (data === 'start') {
            window._stat_track('aisc_create_click', {
                func: 'kt',
            });
        }
        if (data === 'success') {
            window._stat_track('aisc_create_success', {
                func: 'kt',
            });
        }
    };

    useEffect(() => {
        return () => {
            destroyMattingSDK();
            setIsSDKLoaded(false);
        };
    }, []);

    useEffect(() => {
        if (isMatting) {
            window._stat_track('aisc_enter', {
                func: 'kt',
            });
        }
        // 初始化抠图
        if (anchorEl.current && isMatting) {
            if (isSDKLoaded) {
                mattingImage();
            } else {
                Loading.show($t((v) => v.extensions.aiMatting.loading));
                initMattingSDK({
                    anchorEl: anchorEl.current,
                    theme: theme as ThemeType,
                    onload,
                    onDownloadableUpdate,
                    onMattingStatusUpdate,
                });
            }
        }
    }, [isMatting]);

    // 更新主题
    useEffect(() => {
        updateTheme(theme as ThemeType);
    }, [theme]);

    const handleApply = async () => {
        Loading.show($t((v) => v.extensions.aiMatting.loading));
        extractResult();
        editor.imageCutout.close();
    };

    const exitPlugin = (confirmUrl = '') => {
        const { redirectURI } = getQuery();

        if (getQuery().redirectURI) {
            window.location.href = decodeURIComponent(redirectURI) + (confirmUrl ? `&result=${confirmUrl}` : '');
        }

        Loading.hidden();
        mattingPluginHelper.close();
        events.emit(PLUGIN_CLOSE_EVENT);
    };

    const handleClose = () => {
        exitPlugin();
        editor.imageCutout.close();
        events.emit(MATTING_PLUGIN_CANCEL_EVENT);
    };

    return (
        <div
            className={classNames(styles.pluginView, 'matting-plugin-view')}
            style={{ visibility: isMatting ? 'visible' : 'hidden', pointerEvents: 'all' }}
        >
            {isMatting ? (
                <>
                    <AIMattingBackTitle onBackClick={handleClose} />
                    <AIMattingOperateBtns
                        disableApply={!mattingCanApply}
                        isEnterprise={checkTeamIsEnterprise()}
                        onCancelClick={handleClose}
                        onApplyClick={handleApply}
                    />
                </>
            ) : null}
            {/* HACK 兼容暗色模式 */}
            <div
                style={{ visibility: isMatting && isSDKLoaded ? 'visible' : 'hidden' }}
                ref={anchorEl}
                className={styles.anchorEl}
            ></div>
        </div>
    );
};

export default RenderMattingImagePlugin;
